import { useState, useRef, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData.ts';
import ShopsRecycleBin from './components/recyclebin/ShopsRecycleBin';
import ModulesRecycleBin from './components/recyclebin/ModulesRecycleBin';
import TabSelector from '@/components/shared/TabSelector.tsx';
import { DateTime } from 'luxon';

export interface RecycleBinChildRef {
    reload: () => void;
}

const RecycleBin: FCSecurePage = () => {
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState(0);

    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const shopsRef = useRef<RecycleBinChildRef>(null);
    const modulesRef = useRef<RecycleBinChildRef>(null);

    const recycleTabs = [
        { key: 'shops', label: 'shops' },
        { key: 'modules', label: 'modules' }
    ];

    const handleTabChange = (newValue: number) => {
        setTabValue(newValue);
        setLastUpdate(DateTime.now());
    };

    useEffect(() => {
        setLastUpdate(DateTime.now());
    }, []);

    const handleRefresh = useCallback(async () => {
        const currentTab = recycleTabs[tabValue];

        if (currentTab.label === 'shops') {
            shopsRef.current?.reload();
        } else if (currentTab.label === 'modules') {
            modulesRef.current?.reload();
        }
        setLastUpdate(DateTime.now());
    }, [tabValue]);

    useRegisterRefresh(handleRefresh);

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-settings" />}
                title={t('recycleBin')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-shop/recyclebin' }}
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', px: 3, pb: 3 }}>
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabSelector
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="recycle bin tabs"
                        tabs={recycleTabs}
                    >
                        {tabValue === 0 && <ShopsRecycleBin ref={shopsRef} />}
                        {tabValue === 1 && <ModulesRecycleBin ref={modulesRef} />}
                    </TabSelector>
                </Box>
            </Box>
        </Box>
    );
};

RecycleBin.displayName = 'RecycleBin';
export default RecycleBin;
